import { lazy, Suspense, useMemo } from "react";
import { PanelResizeHandle } from "react-resizable-panels";
import { Icon } from "@/components/common/Icon";
import { functionTabShellStyle, tabLabelStyle } from "@/components/common/tab-shell";
import { SegmentationPanel } from "@/components/hitl/segmentation/SegmentationPanel";
import { MonitorPane } from "@/components/monitor/MonitorPane";
import { ProblemsPane } from "@/components/problems/ProblemsPane";
import { SubagentTasksPane } from "@/components/tasks/SubagentTasksPane";
import { TerminalPanel } from "@/components/terminal/TerminalPanel";
import { isTerminalSubagentStatus } from "@/lib/chat";
import { countProblems } from "@/lib/problems";
import { useChatStore } from "@/stores/useChatStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useDiagnosticsStore } from "@/stores/useDiagnosticsStore";
import { useLayoutStore, type BottomPaneTab } from "@/stores/useLayoutStore";

// force-graph (と d3 の一部) はナレッジグラフのタブを開くまで読み込まない。
const KnowledgeGraphPane = lazy(() =>
	import("@/components/knowledge-graph/KnowledgeGraphPane").then((m) => ({
		default: m.KnowledgeGraphPane,
	})),
);

interface BottomPaneTabDef {
	id: BottomPaneTab;
	label: string;
}

const BOTTOM_PANE_TABS: BottomPaneTabDef[] = [
	{ id: "terminal", label: "ターミナル" },
	{ id: "problems", label: "問題" },
	{ id: "monitors", label: "モニター" },
	{ id: "tasks", label: "タスク" },
	{ id: "knowledgeGraph", label: "ナレッジグラフ" },
];

/** セグメンテーションのリクエストがある間だけ、親タブ列の末尾に足すタブ。 */
const SEGMENTATION_TAB: BottomPaneTabDef = { id: "segmentation", label: "セグメンテーション" };

/**
 * 画面下部のペイン本体。親タブの切り替えでターミナルを破棄すると実行中のセッションが失われ、
 * セグメンテーションは編集中の選択・展開状態を失うため、この2つは非表示でも display:none で残す。
 */
export function BottomPane() {
	const bottomPaneTab = useLayoutStore((state) => state.bottomPaneTab);
	const terminalActivated = useLayoutStore((state) => state.terminalActivated);
	const segmentationRequested = useChatStore((state) => state.hitlState?.kind === "segmentation");
	const knowledgeGraphAvailable = useCurrentUserStore((state) => state.knowledgeGraphAvailable);

	return (
		<section
			data-testid="bottom-pane"
			style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
		>
			<BottomPaneTabBar resizable />
			<div
				style={{
					flex: 1,
					minHeight: 0,
					display: bottomPaneTab === "terminal" ? "block" : "none",
				}}
			>
				{terminalActivated && <TerminalPanel />}
			</div>
			{bottomPaneTab === "problems" && (
				<div style={{ flex: 1, minHeight: 0, background: "var(--surface-1)" }}>
					<ProblemsPane />
				</div>
			)}
			{bottomPaneTab === "monitors" && (
				<div style={{ flex: 1, minHeight: 0, background: "var(--surface-1)" }}>
					<MonitorPane />
				</div>
			)}
			{bottomPaneTab === "tasks" && (
				<div style={{ flex: 1, minHeight: 0, background: "var(--surface-1)" }}>
					<SubagentTasksPane />
				</div>
			)}
			{/* 保存済みのタブが使えないタブを指していても (成り代わりなど) 開かない。 */}
			{bottomPaneTab === "knowledgeGraph" && knowledgeGraphAvailable && (
				<div style={{ flex: 1, minHeight: 0, background: "var(--surface-1)" }}>
					<Suspense fallback={null}>
						<KnowledgeGraphPane />
					</Suspense>
				</div>
			)}
			{segmentationRequested && (
				<div
					style={{
						flex: 1,
						minHeight: 0,
						background: "var(--surface-1)",
						display: bottomPaneTab === "segmentation" ? "block" : "none",
					}}
				>
					<SegmentationTabContent />
				</div>
			)}
		</section>
	);
}

/** リクエストが入れ替わったら編集中の状態を作り直すため、tool_use_id を key にする。 */
function SegmentationTabContent() {
	const hitlToolUseId = useChatStore((state) => state.hitlState?.toolUseId);
	return <SegmentationPanel key={hitlToolUseId ?? "empty"} />;
}

/**
 * 下部ペインの親タブ列。ペインを折りたたんでいる間も画面下端に残るため、
 * BottomPane の内側と AppLayout の最下段のどちらか一方で描画される。
 *
 * `resizable` はペイン内側で描画するときだけ渡す。PanelGroup の外で描画される
 * 折りたたみ時の親タブ列には高さ変更のつまみを置けないため。
 */
export function BottomPaneTabBar({ resizable = false }: { resizable?: boolean }) {
	const bottomPaneOpen = useLayoutStore((state) => state.bottomPaneOpen);
	const bottomPaneTab = useLayoutStore((state) => state.bottomPaneTab);
	const openBottomPaneTab = useLayoutStore((state) => state.openBottomPaneTab);
	const setBottomPaneOpen = useLayoutStore((state) => state.setBottomPaneOpen);
	const diagnosticsByPath = useDiagnosticsStore((state) => state.byPath);
	const problemCounts = useMemo(() => countProblems(diagnosticsByPath), [diagnosticsByPath]);
	const problemTotal = problemCounts.error + problemCounts.warning;
	const activeMonitorTotal = useChatStore(
		(state) => (state.monitors ?? []).filter((monitor) => monitor.status === "active").length,
	);
	const activeSubagentRunTotal = useChatStore(
		(state) =>
			(state.subagentRuns ?? []).filter((run) => !isTerminalSubagentStatus(run.status)).length,
	);
	const segmentationRequested = useChatStore((state) => state.hitlState?.kind === "segmentation");
	const tabBadgeTotal: Partial<Record<BottomPaneTab, number>> = {
		problems: problemTotal,
		monitors: activeMonitorTotal,
		tasks: activeSubagentRunTotal,
	};
	const knowledgeGraphAvailable = useCurrentUserStore((state) => state.knowledgeGraphAvailable);
	const availableTabs = knowledgeGraphAvailable
		? BOTTOM_PANE_TABS
		: BOTTOM_PANE_TABS.filter((tab) => tab.id !== "knowledgeGraph");
	const tabs = segmentationRequested ? [...availableTabs, SEGMENTATION_TAB] : availableTabs;

	return (
		<div
			data-testid="bottom-pane-tabs"
			className="pane-header"
			style={{
				position: "relative",
				gap: 0,
				justifyContent: "flex-start",
				minHeight: 32,
				padding: "0 8px",
				background: "var(--surface-1)",
				borderTop: "1px solid var(--line-1)",
			}}
		>
			{/* タブ列の空き領域をつかんで高さを変えられるよう、つまみを行全体に敷く。
			    タブとボタンは前面に重ねてあり、つまみはそこを避けて反応する。
			    zIndex: -1 の理由: react-resizable-panels は前面に別要素があるかを重なり順
			    (z-index 比較、同値なら DOM 順) で判定する。ネイティブ select のドロップダウンは
			    top layer に描画されるが、イベント target の option は DOM 上 select の子のままなので、
			    つまみを負の z-index に下げると option 側が前面と判定され、ドロップダウンに重なった
			    つまみがカーソルやクリックを奪わなくなる。つまみ自体の操作は target がつまみか
			    その祖先になるため影響しない。 */}
			{resizable && (
				<PanelResizeHandle
					data-testid="bottom-pane-resize"
					disabled={!bottomPaneOpen}
					style={{
						position: "absolute",
						inset: 0,
						zIndex: -1,
						cursor: "row-resize",
					}}
				/>
			)}
			{tabs.map(({ id, label }) => {
				const badgeTotal = tabBadgeTotal[id] ?? 0;
				return (
					<button
						key={id}
						type="button"
						data-testid={`bottom-pane-tab-${id}`}
						data-active={bottomPaneOpen && bottomPaneTab === id ? "true" : "false"}
						onClick={() => openBottomPaneTab(id)}
						style={{
							...functionTabShellStyle(bottomPaneOpen && bottomPaneTab === id),
							position: "relative",
							zIndex: 1,
							gap: 6,
							padding: "0 14px",
						}}
					>
						<span style={tabLabelStyle}>{label}</span>
						{badgeTotal > 0 && (
							<span
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: "var(--text-section)",
									padding: "0 6px",
									borderRadius: "var(--radius-full)",
									color: "var(--accent)",
									background: "var(--accent-subtle)",
								}}
							>
								{badgeTotal}
							</span>
						)}
					</button>
				);
			})}
			<button
				type="button"
				data-testid="bottom-pane-toggle"
				data-expanded={bottomPaneOpen ? "true" : "false"}
				onClick={() => setBottomPaneOpen(!bottomPaneOpen)}
				style={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					position: "relative",
					zIndex: 1,
					marginLeft: "auto",
					flexShrink: 0,
					width: 22,
					height: 22,
					border: 0,
					borderRadius: "var(--radius-2)",
					background: "transparent",
					color: "var(--fg-2)",
					cursor: "pointer",
				}}
			>
				<Icon name={bottomPaneOpen ? "chevronD" : "chevronU"} size={14} />
			</button>
		</div>
	);
}
